import { ceil } from "~/tools/ceil";
import { secondsToMilliseconds } from "~/tools/date-time";
import { floor } from "~/tools/floor";
import { min } from "~/tools/min";

import type { TimeRange } from "../app/state/view/timeline/TimeRange";

// distance between each point should be not longer than 6px
const MAXIMUM_POINTS_GAP_PX = 6;
// the minimum step supported by the metrics API
const MINIMUM_STEP_MS = secondsToMilliseconds(1);
// the upper limit of the points dictated by the API endpoint
// it is not reasonable to display as many points anyway
const MAXIMUM_POINTS_COUNT = 30_000;

interface CalculateQueryRangeStepParams {
	timeRange: TimeRange;
	chartWidth: number;
}

export const calculateQueryRangeStep = ({ timeRange, chartWidth }: CalculateQueryRangeStepParams): number => {
	if (chartWidth <= 0) {
		return 0;
	}

	// The points count with the maximum gap between them. It is limited by MAXIMUM_POINTS_COUNT.
	const pointsCount = min(MAXIMUM_POINTS_COUNT, chartWidth / MAXIMUM_POINTS_GAP_PX);

	// Gap between points in milliseconds.
	const pointsGapMs = floor(timeRange.duration() / pointsCount, 0);

	if (pointsGapMs <= MINIMUM_STEP_MS) {
		return MINIMUM_STEP_MS;
	}

	// The length of the gap number in digits.
	const pointsGapDigits = pointsGapMs.toString().length;

	// Keep only the first significant digit and zero out the rest.
	// It prevents refetching data for insignificant changes in the time range selection or chart size.
	return floor(pointsGapMs, 1 - pointsGapDigits);
};

interface CalculateMaxNumberOfPointsParams {
	timeRange: TimeRange;
	chartWidth: number;
	maxNumSeries: number;
}

export const calculateMaxNumberOfPoints = ({
	timeRange,
	chartWidth,
	maxNumSeries,
}: CalculateMaxNumberOfPointsParams): number => {
	const rangeStep = calculateQueryRangeStep({ timeRange, chartWidth });

	if (rangeStep <= 0) {
		return 0;
	}

	const numberOfPoints = ceil(1 + timeRange.duration() / rangeStep, 0);
	return numberOfPoints * maxNumSeries;
};
